import { ChangeDetectorRef, Component, EventEmitter, OnInit, OnDestroy, Output} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { merge, Observable, of, Subject, Subscription, throwError } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';

import { BuilderStateService } from '@fry/components/common/form-builder/builder.state.service';
import { EasBuilderForm, EasWorkflowContext } from '@fry/components/common/form-builder/builder.interfaces';
import { EasFormGroup } from '@fry/components/common/easform';
import { User } from '@fry/lib/users';

import * as TypeGuards from '../lib/type-guards';
import { BackendIntegrationSerializer } from '../lib/backend-integration-serializer';
import { BackendObjectRef } from '../lib/backend-integration.service';
import { FormEditorRoleStateSettings, FormEditorService } from './form-editor.service';
import { State } from '../lib/state.model';
import { WorkflowEditorStore, SelectControlGroup } from '../lib/workflow-editor.store';
import { Workflow } from '../lib/workflow.model';

@Component({
    selector: 'eas-workflow-form-preview',
    templateUrl: './form-preview.component.html',
    styleUrls: ['./form-preview.component.css'],
    providers: [
        BuilderStateService,
    ],
    standalone: false
})
export class WorkflowFormPreviewComponent implements OnInit, OnDestroy {

  @Output() setPermissionForAllFields = new EventEmitter<boolean>()

  public get states(): State[] {
    return this.formEditorService.availableStatesSorted;
  }

  public get isPreviewAvailable(): boolean {
    return this.previewForm !== undefined;
  }

  public get shouldShowBulkAssingButtons(): boolean {
    return this.error === undefined &&
           !this.hasFieldsAssignedForStateRole &&
           this.settings !== undefined;
  }

  public hasFieldsAssignedForStateRole = false;

  public get settings(): FormEditorRoleStateSettings|undefined {
    return this.formEditorService.stateRoleSettings;
  }

  public get settingsRole(): BackendObjectRef|undefined {
    if (!this.settings) { return undefined; }
    return this.formEditorService.availableRoles.find(r => r.id === this.settings?.role );
  }

  public get settingsState(): State|undefined {
    if (!this.settings) { return undefined; }
    return this.formEditorService.availableStates.find(s => s.id === this.settings?.state );
  }

  private _previewForm?: EasFormGroup;
  public get previewForm(): EasFormGroup|undefined {
    return this._previewForm;
  }
  public set previewForm(form: EasFormGroup|undefined) {
    this._previewForm = form;

    if (this._previewForm !== undefined) {
      this.updateAfterPreviewSubscription?.unsubscribe();
      this.updateAfterPreviewSubscription = undefined;
      this.updateAfterPreviewSubscription = this.updateAfterPreviewFormChange(this._previewForm);
    }
  }
  private updateAfterPreviewSubscription?: Subscription;

  public settingsForm: FormGroup;
  public settingsRolesData: SelectControlGroup<BackendObjectRef>[];

  private changeDetectorRef: ChangeDetectorRef;
  public  easBuilderState: BuilderStateService;
  private formEditorService: FormEditorService;
  private workflowEditorStore: WorkflowEditorStore;

  private destroyed$ = new Subject<void>();

  public error?: Error;

  constructor(changeDetectorRef: ChangeDetectorRef,
              formEditorService: FormEditorService,
              formBuilder: FormBuilder,
              easBuilderState: BuilderStateService,
              workflowEditorStore: WorkflowEditorStore) {
    this.changeDetectorRef = changeDetectorRef;
    this.formEditorService = formEditorService;

    this.settingsForm =
      this.createSettingsForm(formBuilder,
                              formEditorService.stateRoleSettings);
    this.easBuilderState = easBuilderState;
    this.workflowEditorStore = workflowEditorStore;
    this.settingsRolesData = this.workflowEditorStore.createRolesSelectData();
  }

  //
  // Lifecycle
  //

  ngOnInit(): void {
    // Push changes from the settings form up to the service
    this.settingsForm.valueChanges
                     .pipe(
                       filter(value => {
                        if (!value.role || !value.state) { return false; }
                        return this.settingsForm.valid;
                       }),
                       takeUntil(this.destroyed$)
                     )
                     .subscribe(value => {
                        this.formEditorService.stateRoleSettings = value;
                     });

    // Update preview based on the Form changes or Settings changes
    merge(
      this.formEditorService.form$,
      this.formEditorService.stateRoleSettings$
    )
    .pipe(
      filter(TypeGuards.isDefined),
      switchMap(_value => {
        return this.createPreviewForm(this.easBuilderState,
                                      this.workflowEditorStore.workflow,
                                      this.formEditorService.stateRoleSettings);
      }),
      takeUntil(this.destroyed$)
    )
    .subscribe(group => {
        this.previewForm = group;
        this.updateAfterPermissionChange();
        this.changeDetectorRef.markForCheck();
    },
    error => {
      console.error(error);
      this.error = error;
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  //
  // Business logic
  //

  private createSettingsForm(formBuilder: FormBuilder,
                             settings?: FormEditorRoleStateSettings): FormGroup {
    const controls = {
      role: [settings?.role ?? '', Validators.required],
      state: [settings?.state ?? '', Validators.required]
    };

    return formBuilder.group(controls);
  }

  private createPreviewForm(builderService: BuilderStateService,
                            workflow: Workflow|undefined,
                            settings: FormEditorRoleStateSettings|undefined): Observable<EasFormGroup|undefined> {

    if (!workflow || !settings) { return of(undefined); }
    try {
      const form: EasBuilderForm
        = (BackendIntegrationSerializer.serializeWorkflow(workflow) as unknown) as EasBuilderForm;

      const context: EasWorkflowContext = {
        state: settings.state, metadata: {}
      };

      // Prepare dummy data & user for the form preview to work
      const data: {[K: string]: string} = { };
      const user: User = new User({
        _rev: undefined,
        _id: 'u-form-preview',
        email: 'formpreview@risr.global',
        firstName: 'Form',
        lastName: 'Preview'
      });

      return builderService.initForPreview(form, {}, data , context, [settings.role], user);
    } catch (error) {
      if (error instanceof TypeError) {
        return throwError(error);
      }
      return of(undefined);
    }
  }

  private updateAfterPermissionChange() {
    this.error = undefined;
    if (!this.formEditorService.stateRoleSettings) { return; }

    const permissions =
      this.formEditorService.form.findPermissionsBy(undefined,
                                                    this.formEditorService.stateRoleSettings.role,
                                                    this.formEditorService.stateRoleSettings.state);
    this.hasFieldsAssignedForStateRole = permissions.length !== 0;
  }

  private updateAfterPreviewFormChange(form: EasFormGroup): Subscription {
    this.error = undefined;
    return form.control.valueChanges
                       .subscribe(_value => {
                           this.changeDetectorRef.markForCheck();
                       });
  }

  //
  // Actions
  //

  public onSetPermissionForAllFields(editable: boolean): void {
    this.setPermissionForAllFields.emit(editable);
  }
}
